import { VoiceCallBusinessPhone, VoiceCallDetails, VoiceCallHandledBy } from '../models/axiosModel';

export interface WabaDetailsFromVoiceCall {
    waba_name: string;
    phone_number: string;
    phone_number_id: string;
}

export const mapBusinessPhoneToWabaDetails = (
    businessPhone?: VoiceCallBusinessPhone | null,
): WabaDetailsFromVoiceCall | null => {
    if (!businessPhone?.phone_number_id) return null;

    return {
        waba_name: businessPhone.verified_name || '',
        phone_number: businessPhone.display_phone_number || '',
        phone_number_id: businessPhone.phone_number_id,
    };
};

export const getHandledByDisplayName = (handledBy?: VoiceCallHandledBy | null): string | null => {
    if (!handledBy) return null;

    if (handledBy.action?.toLowerCase() === 'ai_agent' && handledBy.voice_agent_name) {
        return handledBy.voice_agent_name;
    }

    const name = [handledBy.user_first_name, handledBy.user_last_name]
        .filter(Boolean)
        .join(' ')
        .trim();

    return name || handledBy.user_email || handledBy.phone_number || handledBy.voice_agent_name || null;
};

export const getEndUserDisplayName = (
    voiceCallDetails?: VoiceCallDetails | null,
    fallbackName?: string | null,
): string => {
    const name = voiceCallDetails?.end_user?.name;
    if (name && name.toUpperCase() !== 'UNKNOWN') return name;
    if (fallbackName && fallbackName.toUpperCase() !== 'UNKNOWN') return fallbackName;
    return 'Unknown Caller';
};

export const getEndUserPhoneNumber = (
    voiceCallDetails?: VoiceCallDetails | null,
    fallbackPhone?: string | null,
): string | null => {
    return voiceCallDetails?.end_user?.phone_number || fallbackPhone || null;
};

export const getBusinessPhoneLabel = (
    voiceCallDetails?: VoiceCallDetails | null,
    wabaDetails?: WabaDetailsFromVoiceCall | null,
    direction: 'inbound' | 'outbound' = 'inbound',
): string | null => {
    const businessPhone = voiceCallDetails?.business_phone;
    if (businessPhone?.display_phone_number) {
        const name = businessPhone.verified_name || '';
        const prefix = direction === 'inbound' ? 'Receiving on' : 'Calling from';
        return `${prefix} ${name} ${businessPhone.display_phone_number}`.trim();
    }

    if (wabaDetails?.phone_number) {
        const prefix = direction === 'inbound' ? 'Receiving on' : 'Calling from';
        return `${prefix} ${wabaDetails.waba_name} ${wabaDetails.phone_number}`.trim();
    }

    return null;
};

export const getVoiceCallAcceptedMessage = (voiceCallDetails?: VoiceCallDetails | null): string => {
    const handledByName = getHandledByDisplayName(voiceCallDetails?.handled_by);
    return handledByName ? `Call accepted by ${handledByName}` : 'Call accepted';
};

const getVoiceCallEndedBy = (voiceCallDetails?: VoiceCallDetails | null): VoiceCallHandledBy | null => {
    if (voiceCallDetails?.ended_by) return voiceCallDetails.ended_by;

    const handledBy = voiceCallDetails?.handled_by;
    if (!handledBy) return null;

    const action = handledBy.action?.toLowerCase() ?? '';
    // On COMPLETED events the backend may send the ender in `handled_by` (e.g. ended_by_user)
    // or in a dedicated `ended_by` field. Prefer ended_by; otherwise use handled_by when it looks
    // like an end action, or when the call was handled by an AI voice agent (no separate ended_by).
    if (!action || action.includes('ended') || action === 'ai_agent') return handledBy;

    return null;
};

export const getVoiceCallEndedMessage = (
    callStatus?: string | null,
    voiceCallDetails?: VoiceCallDetails | null,
): string => {
    const status = callStatus?.toLowerCase();

    if (status === 'rejected') return 'Call declined';
    if (status === 'failed') return 'Call failed';

    const endedByName = getHandledByDisplayName(getVoiceCallEndedBy(voiceCallDetails));
    return endedByName ? `Call ended by ${endedByName}` : 'Call ended';
};

export const resolveInboundCallIds = ({
    inboundCallExecutionData,
    voiceCallId,
    callingPersonDetails,
    callingWabaDetails,
    voiceCallDetails,
    getWabaNameAndPhoneNumberBytIntegratedChannelId,
}: {
    inboundCallExecutionData?: { meta_call_id?: string } | null;
    voiceCallId?: string;
    callingPersonDetails?: {
        channel_name?: string;
        integrated_channel_id?: number;
        meta_integration_whatsapp_id?: string | number;
    } | null;
    callingWabaDetails?: WabaDetailsFromVoiceCall | null;
    voiceCallDetails?: VoiceCallDetails | null;
    getWabaNameAndPhoneNumberBytIntegratedChannelId?: (
        integrated_channel_name: string,
        integrated_channel_id: number,
        meta_integration_whatsapp_id?: string | number,
    ) => WabaDetailsFromVoiceCall | null;
}) => {
    const callId = inboundCallExecutionData?.meta_call_id || voiceCallId || '';

    let phoneNumberId = voiceCallDetails?.business_phone?.phone_number_id
        || callingWabaDetails?.phone_number_id
        || '';

    if (!phoneNumberId && callingPersonDetails?.channel_name && callingPersonDetails?.integrated_channel_id && getWabaNameAndPhoneNumberBytIntegratedChannelId) {
        phoneNumberId = getWabaNameAndPhoneNumberBytIntegratedChannelId(
            callingPersonDetails.channel_name,
            callingPersonDetails.integrated_channel_id,
            callingPersonDetails.meta_integration_whatsapp_id,
        )?.phone_number_id || '';
    }

    return { callId, phoneNumberId };
};
